import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { TextField, Button, ButtonGroup, Box, Typography, Grid } from '@mui/material';

import IconButton from '@mui/material/IconButton';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import InputAdornment from '@mui/material/InputAdornment';
import useNotification from '../hooks/useNotification';
import Notification from './Notification';
import FileDropOverlay from './utils/FileDropOverlay';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';

const ValueRenderer = React.memo(({ value }) => {
  if (value === null) {
    return <Typography component="span" sx={{ color: '#AA0099', fontWeight: 'bold' }}>null</Typography>;
  }

  const type = typeof value;

  switch (type) {
    case 'string':
      // Strings in brown/dark orange
      return <Typography component="span" sx={{ color: '#964B00' }}>{value}</Typography>;
    case 'number':
      // Numbers in purple
      return <Typography component="span" sx={{ color: '#AA0099', fontWeight: 'bold' }}>{value}</Typography>;
    case 'boolean':
      // Booleans in purple
      return <Typography component="span" sx={{ color: '#AA0099', fontWeight: 'bold' }}>{value.toString()}</Typography>;
    case 'object':
      // Objects and arrays are handled by the parent JsonViewer, this is a fallback for unexpected objects
      return <Typography component="span" sx={{ color: '#606060' }}>[Object]</Typography>;
    default:
      return <Typography component="span" sx={{ color: '#606060' }}>{String(value)}</Typography>;
  }
});

const JsonViewer = React.memo(({ data, depth = 0 }) => {
  const isArray = Array.isArray(data);
  const entries = useMemo(() => {
    if (typeof data !== 'object' || data === null) return [];
    return isArray ? data.map((item, index) => ({ key: index, value: item })) : Object.entries(data).map(([key, value]) => ({ key, value }));
  }, [data, isArray]);

  const [isExpanded, setIsExpanded] = useState(depth < 10); // Auto-expand first 10 levels

  if (typeof data !== 'object' || data === null || entries.length === 0) {
    return (
      <Box sx={{ ml: depth * 2, pl: 1, display: 'inline-block' }}>
        <ValueRenderer value={data} />
      </Box>
    );
  }

  const toggleExpand = (e) => {
    e.stopPropagation();
    setIsExpanded(prev => !prev);
  };

  const openBracket = isArray ? '[' : '{';
  const closeBracket = isArray ? ']' : '}';

  return (
    <Box sx={{
      // Indentation based on depth, but we don't apply it to depth 0
      ml: depth > 0 ? depth * 1.5 : 0,
      paddingLeft: depth > 0 ? '8px' : 0,
      borderLeft: depth > 0 ? '1px solid #e0e0e0' : 'none',
      width: '100%', // Ensure it takes full width for block-level nesting
    }}>
      {/* Structural element: Opening Bracket */}
      <Box
        onClick={toggleExpand}
        sx={{
          display: 'flex',
          alignItems: 'center',
          cursor: 'pointer',
          '&:hover .key-label': {
            textDecoration: 'underline',
          },
        }}
      >
        <Typography component="span" sx={{ color: '#606060', mr: 0.5 }}>
          <FiberManualRecordIcon
            fontSize="inherit"
            sx={{
              transition: 'transform 0.1s',
              transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
              color: '#3f51b5', // Indicator color
            }}
          />
        </Typography>
        <Typography component="span" sx={{ color: '#606060', fontWeight: 'bold' }}>
          {openBracket}
        </Typography>
        {!isExpanded && (
          <Typography component="span" className="key-label" sx={{ color: '#606060', ml: 1, fontStyle: 'italic' }}>
            ... {entries.length} {isArray ? 'items' : 'keys'} ...
          </Typography>
        )}
      </Box>

      {/* Content: Key-Value Pairs */}
      {isExpanded && (
        <Box sx={{ pl: 1, width: '100%' }}>
          {entries.map(({ key, value }, index) => {
            const isNested = (typeof value === 'object' && value !== null);
            // FIX: Use depth and index to create a globally unique key for React lists
            const reactKey = `viewer-item-${depth}-${index}`;

            // Key and Colon element (Always rendered for consistent alignment)
            const KeyColonElement = (
              <Box component="span" sx={{ flexShrink: 0 }}>
                {isArray ? (
                  <Typography component="span" sx={{ color: '#606060' }}>{key}</Typography>
                ) : (
                  <Typography component="span" sx={{ color: '#2864C6', fontWeight: 'bold' }}>
                    {key}
                  </Typography>
                )}
                {/* Always include colon and spacing */}
                <Typography component="span" sx={{ color: '#606060', mr: 1.5 }}>
                  :
                </Typography>
              </Box>
            );

            // Conditional rendering logic
            if (isNested) {
              // Nested object/array: Render Key/Colon, then the nested viewer on a new block line
              return (
                <Box key={reactKey} sx={{ display: 'block' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    {KeyColonElement}
                  </Box>

                  {/* Nested Viewer starts on the next line (and provides its own indentation/box) */}
                  <JsonViewer data={value} depth={depth + 1} />

                  {/* Comma after the nested block ends */}
                  {index < entries.length - 1 && (
                    <Typography component="div" sx={{ color: '#606060', mt: 0 }}>
                      ,
                    </Typography>
                  )}
                </Box>
              );
            }

            // Primitive value: Use flex to keep key and value inline
            return (
              <Box key={reactKey} sx={{ display: 'flex', alignItems: 'flex-start' }}>
                {KeyColonElement}
                <ValueRenderer value={value} />
                {/* Comma Separator */}
                {index < entries.length - 1 && (
                  <Typography component="span" sx={{ color: '#606060' }}>
                    ,
                  </Typography>
                )}
              </Box>
            );
          })}
        </Box>
      )}

      {/* Structural element: Closing Bracket */}
      <Typography component="span" sx={{ color: '#606060', fontWeight: 'bold', ml: depth > 0 ? '8px' : 0 }}>
        {closeBracket}
        {depth === 0 && (
          <Typography component="span" sx={{ color: '#606060', ml: 1, fontWeight: 'normal' }}>
            {entries.length} {isArray ? 'items' : 'keys'} total
          </Typography>
        )}
      </Typography>
    </Box>
  );
});

const JsonMaker = () => {
  const [inputText, setInputText] = useState('');
  const [outputJsonString, setOutputJsonString] = useState('');
  const [parsedOutputJson, setParsedOutputJson] = useState(null);
  const [jsonFormat, setJsonFormat] = useState('prettify');

  const [charCount, setCharCount] = useState(0);
  const [keyCount, setKeyCount] = useState(0);
  const [valueCount, setValueCount] = useState(0);
  const [isDragging, setIsDragging] = useState(false);

  const { snackbarOpen, snackbarMessage, handleCopy, showNotification, handleCloseSnackbar } = useNotification();

  const handleFileDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = 'copy';
    setIsDragging(true);
  };

  const handleFileDragLeave = (e) => {
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleFileDrop = (e) => {
    e.preventDefault(); // Prevent default browser behavior (e.g., opening file)
    e.stopPropagation();
    setIsDragging(false);

    const files = e.dataTransfer.files;
    if (files.length > 0) {
      const file = files[0];
      const reader = new FileReader();

      reader.onload = (event) => {
        setInputText(event.target.result);
        showNotification(`File "${file.name}" loaded successfully!`);
      };

      reader.onerror = () => {
        showNotification(`Error reading file "${file.name}".`);
      };

      reader.readAsText(file);
    } else {
      showNotification('No file dropped or invalid drop operation.');
    }
  };

  // Helper function to recursively count keys and values in a JSON object/array
  const countJsonItems = useCallback((data) => {
    let keys = 0;
    let values = 0;

    // Base cases for non-object/array types
    if (typeof data !== 'object' || data === null) {
      return { keys: 0, values: 1 };
    }

    // Handle arrays
    if (Array.isArray(data)) {
      // The keys are the array indices, but we'll count them as "items"
      // The values are the elements within the array
      values = data.length;
      data.forEach(item => {
        const result = countJsonItems(item);
        keys += result.keys;
      });
      return { keys, values };
    }

    // Handle objects
    const objKeys = Object.keys(data);
    keys = objKeys.length;

    objKeys.forEach(key => {
      const value = data[key];
      const result = countJsonItems(value);
      keys += result.keys;
      values += result.values;
    });

    return { keys, values };
  }, []);

  const convertToFormattedJson = useCallback(() => {
    try {
      if (!inputText) {
        setOutputJsonString('');
        setParsedOutputJson(null);
        setCharCount(0);
        setKeyCount(0);
        setValueCount(0);
        return;
      }
      setCharCount(inputText.length);

      const parsedJson = JSON.parse(inputText);
      let formattedJson;

      if (jsonFormat === 'prettify') {
        formattedJson = JSON.stringify(parsedJson, null, 2);
        setParsedOutputJson(parsedJson);
      } else {
        formattedJson = JSON.stringify(parsedJson);
        setParsedOutputJson(null);
      }

      setOutputJsonString(formattedJson);

      const counts = countJsonItems(parsedJson);
      setKeyCount(counts.keys);
      setValueCount(counts.values);

    } catch (error) {
      // If the input text is not valid JSON, display the error message
      setOutputJsonString('Error: Invalid JSON format. Please check your input.');
      setParsedOutputJson(null);
      // setCharCount(0);
      setKeyCount(0);
      setValueCount(0);
    }
  }, [inputText, jsonFormat, countJsonItems]);

  // Trigger conversion whenever relevant state changes
  useEffect(() => {
    convertToFormattedJson();
  }, [convertToFormattedJson]);

  // Determine if the output is an error message
  const isError = outputJsonString.startsWith('Error');

  // Logic to determine what to render in the Output section
  const shouldShowViewer = jsonFormat === 'prettify' && parsedOutputJson && !isError;

  return (
    <Grid container spacing={4} direction="column">
      {/* Grid container for responsive layout */}
      <Grid item xs={12}>
        {/* Main container for all option rows, using vertical flexbox */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 1.5,
            marginBottom: 1,
          }}
        >
          {/* Row 1: Stringify/Prettify  */}
          <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
            {/* Fixed-width label forces controls into alignment */}
            <Typography
              variant="button"
              sx={{
                width: { xs: '100%', sm: '170px' }, // Full width on mobile, fixed on desktop
                flexShrink: 0,
              }}
            >
              Select JSON Output:
            </Typography>
            {/* Container for the controls */}
            <ButtonGroup variant="outlined" aria-label="JSON format options">
              <Button
                onClick={() => setJsonFormat('prettify')}
                variant={jsonFormat === 'prettify' ? 'contained' : 'outlined'}
              >
                Prettify
              </Button>
              <Button
                onClick={() => setJsonFormat('stringify')}
                variant={jsonFormat === 'stringify' ? 'contained' : 'outlined'}
              >
                Stringify
              </Button>
            </ButtonGroup>
          </Box>
        </Box>
      </Grid>

      {/* Input Section */}
      <Grid item xs={12}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 2, // Spacing within input section
            marginBottom: 1, // Space below input before output
          }}
        >
          <Box
            sx={{
              position: 'relative',
              width: '100%',
              height: 'auto',
            }}
            onDragOver={handleFileDragOver}
            onDragLeave={handleFileDragLeave}
            onDrop={handleFileDrop}
          >
            {isDragging && <FileDropOverlay />}
            {/* Text field for user input */}
            <TextField
              label={
                inputText.length
                  ? `Input JSON — ${charCount} characters`
                  : !isDragging && "Drag & drop file or type/paste json"
              }

              multiline
              rows={8}
              variant="outlined"
              fullWidth
              value={inputText}
              onChange={(e) => setInputText(e.target.value)}
              placeholder={
                !isDragging && `Enter JSON here\ne.g:\n\n{"name": "John", "age": 30}`
              }
              sx={{
                borderRadius: 1,
                '& .MuiInputBase-input': { paddingRight: '45px' }
              }}
              slotProps={{
                input: {
                  endAdornment: inputText.length > 0 && (
                    <InputAdornment position="end" sx={{ position: 'absolute', top: 15, right: 22, zIndex: 5 }}>
                      <IconButton
                        onClick={() => handleCopy(inputText, 'Input copied to clipboard!')}
                        size="small"
                        disabled={!inputText.length || isError}>
                        <ContentCopyIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ),
                },
              }}
            />
          </Box>
        </Box>
      </Grid>

      {/* Output Section */}
      {outputJsonString.length > 0 && (
        <Grid item xs={12}>
          <Typography
            sx={{ fontSize: 'small' }}
            variant="outlined">Output JSON (Keys: {keyCount} Values: {valueCount})</Typography>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
              mt: 1,
              position: 'relative', // For absolute positioning of the copy button
            }}
          >
            {/* Copy Button (Positioned over the output area) */}
            <InputAdornment position="end" sx={{ position: 'absolute', top: 15, right: 22, zIndex: 5 }}>
              <IconButton
                onClick={() => handleCopy(outputJsonString, 'Output copied to clipboard!')}
                size="small"
                disabled={!inputText.length || isError}
                sx={{
                  '&:hover': { backgroundColor: '#eee' }
                }}
              >
                <ContentCopyIcon fontSize="small" />
              </IconButton>
            </InputAdornment>

            {/* Conditional Output Display */}
            {shouldShowViewer ? (
              // Mode 1: Visual Viewer (Using a Box styled like a read-only TextField)
              <Box
                sx={{
                  border: '1px solid #c4c4c4',
                  borderRadius: '4px',
                  p: 2,
                  minHeight: '200px',
                  paddingTop: '16px',
                  paddingBottom: '16px',
                  paddingLeft: '14px',
                  paddingRight: '14px',
                  overflow: 'auto',
                  lineHeight: 1,
                }}
              >
                <JsonViewer data={parsedOutputJson} />
              </Box>
            ) : (
              // Mode 2: String/Error Output (Using actual TextField)
              <TextField
                multiline
                rows={8}
                variant="outlined"
                fullWidth
                value={inputText.length ? outputJsonString : ''}
                SlotProps={{
                  readOnly: true,
                  style: {
                    whiteSpace: 'pre-wrap',
                    color: isError ? 'red' : 'inherit'
                  },
                }}
                // label="Output JSON (Prettified/Stringified)"
                sx={{
                  borderRadius: 1,
                  '& .MuiInputBase-root': {
                    paddingTop: '1em',
                    paddingBottom: '0px',
                  },
                  '& .MuiInputBase-input': { paddingRight: '45px' }
                }}
              />
            )}
          </Box>
          <Notification
            open={snackbarOpen}
            message={snackbarMessage}
            handleClose={handleCloseSnackbar}
          />
        </Grid>
      )}
    </Grid>
  );
};

export default JsonMaker;